import { Injectable } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import {
  AccessPermissionService,
  dblclickTooltipDef,
  defaultColumn,
  DELETE_RECORD_LABEL,
  FeatureCodes,
  grid_widths_map,
  mainModuleName,
  rolesModuleName,
} from '@piqet-angular/shared';
import { right } from '@popperjs/core';
import { CustomFacade, getRolesAndPermission, isFeatureEnabled } from '@piqet-angular/data';
import { cloneDeep } from 'lodash';
import { NumericCellEditor } from 'libs/ui/src/lib/numeri-cell-editor/numeric-cell-editor.component';
import { PremiumHeaderComponent } from 'libs/ui/src/lib/premium-header/premium-header.component';
import { Subject, take } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { SwalService } from 'libs/shared/src/lib/service/swal.service';
@Injectable({
  providedIn: 'root',
})
export class CustomMaterialsDataGridService {
  customMaterialPayload: any;
  event: any;
  columnDefs: any;
  Access: any;

  editMaterialSub = new Subject<[any, any, any, any]>();
  customMaterialObservable$ = this.editMaterialSub.asObservable();

  deleteMaterialSub = new Subject<[any, any, any]>();
  deleteCustomMaterialObservable$ = this.deleteMaterialSub.asObservable();

  copyMaterialSub = new Subject<[any, any, any]>();
  copyCustomMaterialObservable$ = this.copyMaterialSub.asObservable();

  inlineEditing = new Subject<[any, any]>();
  inlineEditingObservable$ = this.inlineEditing.asObservable();
  currentRoute: string = '';
  constructor(
    private CustomFacade: CustomFacade,
    private toaster: ToastrService,
    private swalService: SwalService,
    private accessService: AccessPermissionService
  ) {}

  accessAllPermission(type: string) {
    const access =
      this.isAccess(type, 'edit') || this.isAccess(type, 'delete') || this.isAccess(type, 'create');
    return access;
  }

  getCustomMaterialDataColumn(pathName?: any): ColDef[] {
    this.currentRoute = pathName;
    const colDef: ColDef[] = [];
    colDef.push({
      ...defaultColumn,
      colId: 'Name',
      headerName: 'My custom materials',
      field: 'name',
      width: 468,
      cellRenderer: (params: any) => {
        const cellValue = params.value;
        const cellElement = document.createElement('span');
        cellElement.style.cursor = 'pointer';
        cellElement.innerHTML = `${cellValue}`;
        const checkAccess = this.isAccess(pathName, 'edit') || this.isAccess(pathName, 'view');
        if (checkAccess) {
          cellElement.title = dblclickTooltipDef;
          cellElement.style.textDecoration = 'underline';
        }
        cellElement.addEventListener('dblclick', () => {
          checkAccess &&
            this.editMaterialSub.next([
              params.data,
              params.data.customMaterialId,
              'Review custom material',
              pathName,
            ]);
        });
        return cellElement;
      },
      wrapText: true,
      autoHeight: true,
      resizable: false,
      editable: false,
      filter: 'agTextColumnFilter',
      lockVisible: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Description',
      headerName: 'Description',
      field: 'description',
      width: 468,
      valueSetter: (params: any) => {
        const newVal = params.newValue;
        const valueChanged = params.data[params.colDef.field] !== newVal;
        params = cloneDeep(params);
        if (valueChanged) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
        }
        const field = params.colDef.field;
        const nodeId = params.data.id;
        const inlineObject = {
          entityId: 'customMaterialId',
          entityIdValue: params.data['customMaterialId'],
          entityColumnName: params.colDef.field,
          entityColumnValue: params.data[params.colDef.field],
        };
        this.inlineEditing.next([inlineObject, 'CustomMaterial']);
        return valueChanged;
      },
      cellRendererParams: {
        suppressDoubleClickExpand: true,
      },
      cellRenderer: (params: any) => {
        const extraClass =
          pathName === 'adminCustomMaterials'
            ? 'editable-icon-padding-admin-custom-menu'
            : 'editable-icon-padding';
        return `<span class="editable-icon ${extraClass}" title="${params.value}">${
          params.value ? params.value : ''
        }<i class="fa fa-pencil icon-opacity" aria-hidden="true"></i></span>`;
      },
      cellClass: ['outerGridEdit', 'elipsisFields'],
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
      filter: 'agTextColumnFilter',
      editable: this.isAccess(pathName, 'edit'),
    });
    {
      pathName == 'adminCustomMaterials' &&
        colDef.push({
          ...defaultColumn,
          colId: 'OrganizationName',
          headerName: 'Organization name',
          field: 'organizationName',
          maxWidth: grid_widths_map.sm_column_xl,
          minWidth: grid_widths_map.sm_column_sm,
          wrapText: true,
          autoHeight: true,
          filter: 'agTextColumnFilter',
          lockVisible: true,
          resizable: false,
          editable: false,
        });
    }

    colDef.push({
      ...defaultColumn,
      colId: 'OwnerName',
      headerName: 'Owner',
      field: 'ownerName',
      width: 468,
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
      filter: 'agTextColumnFilter',
      editable: false,
    });

    this.accessAllPermission(pathName) &&
      colDef.push({
        ...defaultColumn,
        colId: 'actions',
        headerName: 'Actions',
        field: 'actions',
        sortable: false,
        maxWidth: grid_widths_map.sm_column_xs,
        suppressColumnsToolPanel: true,
        cellRenderer: 'actionRenderer',
        cellRendererParams: {
          showEdit: this.isAccess(pathName, 'edit'),
          showCopy: this.isAccess(pathName, 'create'),
          showDelete: this.isAccess(pathName, 'delete'),
          value: 'customMaterials',
          deleteData: (data: any, pathName: any) => {
            if (pathName == 'adminCustomMaterials') {
              this.deleteCustomConfirmation(data, '/AdminCustomMaterial/DeleteMaterial', pathName);
            } else {
              this.deleteCustomConfirmation(data, '/CustomMaterial/DeleteMaterial', pathName);
            }
          },
          copyData: (data: any, pathName: any) => {
            if (pathName == 'adminCustomMaterials') {
              this.copyMaterialSub.next([
                data,
                '/AdminCustomMaterial/SaveCloneCustomMaterial',
                pathName,
              ]);
            } else {
              this.copyMaterialSub.next([
                data,
                '/CustomMaterial/SaveCloneCustomMaterial',
                pathName,
              ]);
            }
          },
          editData: (data: any, pathName: any) => {
            this.editMaterialSub.next([
              data,
              data.customMaterialId,
              'Review custom material',
              pathName,
            ]);
          },
          copyDataEditRendarar: (data: any) => {
            return false;
          },
        },
        lockVisible: true,
        lockPosition: right,
        resizable: false,
      });

    return colDef;
  }

  isAccess = (pathName: any, type: 'view' | 'edit' | 'create' | 'delete') => {
    return pathName.includes('admin')
      ? this.accessService.hasModulePermission(
          rolesModuleName.AdministratorMaterials,
          mainModuleName.projectManagementModule,
          type
        )
      : this.accessService.hasModulePermission(
          rolesModuleName.customMaterial,
          mainModuleName.customModule,
          type
        );
  };

  deleteCustomConfirmation(data: any, customType: any, pathName: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.deleteMaterialSub.next([data, customType, pathName]);
      }
    });
  }

  innedGridActionAccess(fromEdit: boolean): boolean {
    return (
      (!fromEdit && this.isAccess(this.currentRoute, 'create')) ||
      (fromEdit && !this.isAccess(this.currentRoute, 'view')) ||
      (fromEdit && this.isAccess(this.currentRoute, 'edit'))
    );
  }

  getMaterialIngredientColumns(fromEdit: boolean = false): ColDef[] {
    let newValue: any;
    let materialTypeId: any;
    let enableEditing: any;
    let resetData: any;
    let endofLifeIdentifierName: any;
    let endofLifeIdentifierId: any;

    const colDef: ColDef[] = [];
    colDef.push({
      ...defaultColumn,
      colId: 'material',
      headerName: 'Material *',
      field: 'materialName',
      maxWidth: grid_widths_map.sm_column_lg,
      minWidth: grid_widths_map.sm_column_lg,
      cellStyle: { 'padding-right': '34px' },
      cellClass: 'truncated-text material-ingredient-arrow',
      cellEditor: 'GridCellRendererComponent',
      //cellRenderer: 'GridCellRendererComponent',
      cellRenderer: (params: any) => {
        return `<div class="truncated-text" title="${params.value}">${
          params.value ? params.value : ''
        }</div>`;
      },
      cellEditorParams: {
        from: 'materialIngridiant',
        editData: (data: any) => {
          newValue = data.materialName;
          materialTypeId = data.materialTypeId;
          // this.CustomFacade.updateMaterialIngredientActions(data, 'edit')
        },
      },
      valueSetter: (params: any) => {
        const newVal = !!newValue && !resetData ? newValue : 'Select material';
        const valueChanged =
          !!newValue && !resetData ? params.data[params.colDef.field] !== newVal : false;
        params.data = { ...params.data };
        if (valueChanged) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
          params.data['materialTypeId'] = materialTypeId;
          params.data['enableEditing'] = enableEditing;
          const colId = params.colDef.field;
          const nodeId = params.data.id;
          this.CustomFacade.checkUpdateActionFire$.pipe(take(1)).subscribe((flag: boolean) => {
            flag &&
              this.CustomFacade.updateMaterialIngredientActions(
                params.data,
                'edit',
                null,
                nodeId,
                colId
              );
          });
          newValue = '';
          materialTypeId = '';
        }
        return valueChanged;
      },
      valueGetter: params => {
        return params.data.materialName;
      },
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      resizable: false,
      editable: true,
      sortable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'weight',
      headerName: 'Weight (gm) *',
      field: 'weight',
      suppressColumnsToolPanel: true,
      wrapText: false,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      resizable: false,
      editable: true,
      sortable: false,
      maxWidth: grid_widths_map.sm_column_xm,
      cellEditorSelector: (params: any) => {
        if (params.colDef.colId === 'weight') {
          return {
            component: NumericCellEditor,
          };
        }
        return undefined;
      },
      cellEditorParams: {
        from: 'materialIngredient-weight',
      },
      valueSetter: (params: any) => {
        const newVal = params.newValue;
        const valueChanged = !resetData && params.data[params.colDef.field] !== newVal;
        params.data = { ...params.data };
        if (valueChanged) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
          params.data['enableEditing'] = enableEditing;
        }
        const colId = params.colDef.colId;
        const nodeId = params.data.id;
        this.CustomFacade.checkUpdateActionFire$.pipe(take(1)).subscribe((flag: boolean) => {
          flag &&
            this.CustomFacade.updateMaterialIngredientActions(
              params.data,
              'edit',
              null,
              nodeId,
              colId
            );
        });
        return false;
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'recycledContent',
      headerName: 'Recycled content (%)',
      field: 'recycledContent',
      // cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      lockPosition: true,
      resizable: false,
      editable: true,
      sortable: false,
      cellEditorSelector: (params: any) => {
        if (params.colDef.colId == 'recycledContent') {
          return {
            component: NumericCellEditor,
          };
        }
        return undefined;
      },
      cellEditorParams: {
        from: 'materialIngredient-recycledContent',
      },
      valueSetter: (params: any) => {
        const newVal = params.newValue;
        const valueChanged = !resetData && params.data[params.colDef.field] !== newVal;
        params.data = { ...params.data };
        if (valueChanged) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
          params.data['enableEditing'] = enableEditing;
        }
        const colId = params.colDef.colId;
        const nodeId = params.data.id;
        this.CustomFacade.checkUpdateActionFire$.pipe(take(1)).subscribe((flag: boolean) => {
          flag &&
            this.CustomFacade.updateMaterialIngredientActions(
              params.data,
              'edit',
              null,
              nodeId,
              colId
            );
        });
        return false;
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'scrapedWeight',
      headerName: 'Weight of scrap generated (gm)',
      field: 'scrapedWeight',
      // cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      lockPosition: true,
      resizable: false,
      editable: true,
      sortable: false,
      cellEditorSelector: (params: any) => {
        if (params.colDef.colId === 'scrapedWeight') {
          return {
            component: NumericCellEditor,
          };
        }
        return undefined;
      },
      cellEditorParams: {
        from: 'materialIngredient-scrapedWeight',
      },
      valueSetter: (params: any) => {
        const newVal = params.newValue;
        const valueChanged = !resetData && params.data[params.colDef.field] !== newVal;
        params.data = { ...params.data };
        if (valueChanged) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
          params.data['enableEditing'] = enableEditing;
        }
        const colId = params.colDef.colId;
        const nodeId = params.data.id;
        this.CustomFacade.checkUpdateActionFire$.pipe(take(1)).subscribe((flag: boolean) => {
          flag &&
            this.CustomFacade.updateMaterialIngredientActions(
              params.data,
              'edit',
              null,
              nodeId,
              colId
            );
        });
        return valueChanged;
      },
    });
    colDef.push({
      ...defaultColumn,
      colId: 'eol',
      headerName: 'Destination of scrap',
      field: 'endofLifeIdentifierName',
      maxWidth: grid_widths_map.sm_column_lg,
      minWidth: grid_widths_map.sm_column_lg,
      cellStyle: { 'padding-right': '34px' },
      cellClass: 'truncated-text',
      cellEditor: 'GridCellRendererComponent',
      cellRenderer: (params: any) => {
        return `<div class="truncated-text" title="${params.value}">${
          params.value ? params.value : ''
        }</div>`;
      },
      cellEditorParams: {
        from: 'materialEolIdentifier',
        editData: (data: any) => {
          endofLifeIdentifierName = data.endofLifeIdentifierName;
          endofLifeIdentifierId = data.endofLifeIdentifierId;
        },
      },
      valueSetter: (params: any) => {
        const newVal = !!endofLifeIdentifierName && !resetData ? endofLifeIdentifierName : '';
        const valueChanged =
          !!endofLifeIdentifierName && !resetData
            ? params.data[params.colDef.field] !== newVal
            : false;
        params.data = { ...params.data };
        if (valueChanged) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
          params.data['endofLifeIdentifierId'] = endofLifeIdentifierId;
          params.data['enableEditing'] = enableEditing;
          const colId = params.colDef.field;
          const nodeId = params.data.id;
          this.CustomFacade.checkUpdateActionFire$.pipe(take(1)).subscribe((flag: boolean) => {
            flag &&
              this.CustomFacade.updateMaterialIngredientActions(
                params.data,
                'edit',
                null,
                nodeId,
                colId
              );
          });
          endofLifeIdentifierName = '';
          endofLifeIdentifierId = null;
        }
        return valueChanged;
      },
      valueGetter: params => {
        return params.data.endofLifeIdentifierName;
      },
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      resizable: false,
      editable: true,
      sortable: false,
    });
    const isCO2EFValueEnabled = isFeatureEnabled(FeatureCodes.CUSTOM_MATERIAL_CO2_EF_VALUE);
    colDef.push({
      ...defaultColumn,
      colId: 'factorValue',
      headerName: 'CO₂eq/gram',
      headerComponent: PremiumHeaderComponent,
      headerComponentParams: {
        isPremiumFeature: true,
        featureEnabled: isCO2EFValueEnabled,
        featureCode: FeatureCodes.CUSTOM_MATERIAL_CO2_EF_VALUE,
      },
      field: 'factorValue',
      // cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      lockPosition: true,
      resizable: false,
      editable: isCO2EFValueEnabled,
      sortable: false,
      cellEditorSelector: (params: any) => {
        if (params.colDef.colId === 'factorValue') {
          return {
            component: NumericCellEditor,
          };
        }
        return undefined;
      },
      cellEditorParams: {
        from: 'materialIngredient-factorValue',
      },
      valueSetter: (params: any) => {
        if (!isCO2EFValueEnabled) return false;
        const newVal = params.newValue;
        const valueChanged = !resetData && params.data[params.colDef.field] !== newVal;
        params.data = { ...params.data };
        if (valueChanged) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
          params.data['enableEditing'] = enableEditing;
        }
        const colId = params.colDef.colId;
        const nodeId = params.data.id;
        this.CustomFacade.checkUpdateActionFire$.pipe(take(1)).subscribe((flag: boolean) => {
          flag &&
            this.CustomFacade.updateMaterialIngredientActions(
              params.data,
              'edit',
              null,
              nodeId,
              colId
            );
        });
        return valueChanged;
      },
    });

    this.innedGridActionAccess(fromEdit) &&
      colDef.push({
        ...defaultColumn,
        colId: 'actions',
        headerName: 'Actions',
        field: 'actions',
        sortable: false,
        maxWidth: grid_widths_map.sm_column_xs,
        cellRenderer: 'inlineActionButtonComponent',
        cellEditorParams: {
          showEdit: false,
          editData: (data: any) => {
            // this.CustomFacade.updateMaterialIngredientActions(data, 'edit')
          },
        },
        cellRendererParams: {
          // showEdit: false,
          // showCopy: true,
          // showDelete: true,
          from: 'customMaterialIngredient',
          showInlineEdit: true,
          showInlineCopy: true,
          showInlineDelete: true,
          copyDataEditRendarar: (data: any) => {
            this.CustomFacade.copyMaterialIngredientActions(data, 'copy');
          },
          // editData: (data: any) => {
          //   this.CustomFacade.updateMaterialIngredientActions(data, 'edit')
          // },
          inlineEditData: (data: any) => {
            enableEditing = data.data.enableEditing;
            resetData = data.data.resetData;
          },
          deleteInlineGrid: (data: any) => {
            this.alertConfirmationForIngrediant(data);
          },
          copyData: (data: any) => {
            return false;
          },
        },
        lockVisible: true,
        lockPosition: right,
        resizable: false,
        pinned: right,
      });
    return colDef;
  }

  alertConfirmationForIngrediant(data: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.CustomFacade.deleteMaterialIngredientActions(data, 'delete');
      }
    });
  }

  getNewProcessColumns(): ColDef[] {
    let newValue: any;
    let energyId: any;
    let enableEditing: any;
    let resetData: any;
    const colDef: ColDef[] = [];
    colDef.push({
      ...defaultColumn,
      colId: 'process',
      headerName: 'Process name',
      field: 'processName',
      // cellRenderer: 'dropdownRendered',
      cellEditor: 'GridCellRendererComponent',
      cellEditorParams: {
        from: 'materialProcess',
        editData: (data: any) => {
          newValue = data.processName;
          energyId = data.energyId;
        },
      },
      cellRenderer: (params: any) => {
        return `<div class="truncated-text" title="${params.value}">${
          params.value ? params.value : ''
        }</div>`;
      },
      valueSetter: (params: any) => {
        const newVal = !!newValue ? newValue : 'Select material process';
        const valueChanged =
          !!newValue && !resetData ? params.data[params.colDef.field] !== newVal : false;
        params.data = { ...params.data };
        if (valueChanged) {
          params.data['enableEditing'] = enableEditing;
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
          params.data['energyId'] = energyId;
          const colId = params.colDef.field;
          const nodeId = params.data.id;
          this.CustomFacade.checkUpdateActionFire$.pipe(take(1)).subscribe((flag: boolean) => {
            flag &&
              this.CustomFacade.updateMaterialProcessActions(
                params.data,
                'edit',
                null,
                nodeId,
                colId
              );
          });
          newValue = '';
          energyId = '';
        }
        return valueChanged;
      },
      valueGetter: params => {
        return params.data.processName;
      },
      wrapText: true,
      autoHeight: true,
      width: 100,
      lockVisible: true,
      lockPosition: true,
      resizable: false,
      editable: true,
      sortable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'amount',
      headerName: 'Amount',
      field: 'amount',
      suppressColumnsToolPanel: true,
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      resizable: false,
      editable: true,
      sortable: false,
      cellEditorSelector: (params: any) => {
        if (params.colDef.colId === 'amount') {
          return {
            component: NumericCellEditor,
          };
        }
        return undefined;
      },
      cellEditorParams: {
        from: 'materialIngredient-amount',
      },
      valueSetter: (params: any) => {
        const newVal = params.newValue;
        const valueChanged = !resetData && params.data[params.colDef.field] !== newVal;
        params.data = { ...params.data };
        if (valueChanged) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
          params.data['enableEditing'] = enableEditing;
        }
        const colId = params.colDef.field;
        const nodeId = params.data.id;
        this.CustomFacade.checkUpdateActionFire$.pipe(take(1)).subscribe((flag: boolean) => {
          flag &&
            this.CustomFacade.updateMaterialProcessActions(
              params.data,
              'edit',
              null,
              nodeId,
              colId
            );
        });
        return valueChanged;
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'comment',
      headerName: 'Comment',
      field: 'comment',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      lockPosition: true,
      resizable: false,
      editable: true,
      sortable: false,
      valueSetter: (params: any) => {
        const newVal = params.newValue;
        const valueChanged = !resetData && params.data[params.colDef.field] !== newVal;
        params.data = { ...params.data };
        if (valueChanged) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
          params.data['enableEditing'] = enableEditing;
        }
        const colId = params.colDef.colId;
        const nodeId = params.data.id;
        this.CustomFacade.checkUpdateActionFire$.pipe(take(1)).subscribe((flag: boolean) => {
          flag &&
            this.CustomFacade.updateMaterialProcessActions(
              params.data,
              'edit',
              null,
              nodeId,
              colId
            );
        });
        return valueChanged;
      },
    });

    this.isAccess(this.currentRoute, 'edit') &&
      colDef.push({
        ...defaultColumn,
        colId: 'actions',
        headerName: 'Actions',
        field: 'actions',
        sortable: false,
        maxWidth: grid_widths_map.sm_column_xs,
        cellRenderer: 'inlineActionButtonComponent',
        cellEditorParams: {
          showEdit: false,
          editData: (data: any) => {
            this.CustomFacade.updateMaterialProcessActions(data, 'edit');
          },
        },
        cellRendererParams: {
          showInlineEdit: true,
          showInlineDelete: true,
          from: ' ',
          inlineEditData: (data: any) => {
            enableEditing = data.data.enableEditing;
            resetData = data.data.resetData;
          },
          deleteInlineGrid: (data: any) => {
            this.alertConfirmation(data);
          },
        },
        lockVisible: true,
        lockPosition: right,
        resizable: false,
        pinned: right,
      });

    return colDef;
  }

  alertConfirmation(data: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.CustomFacade.deleteMaterialProcessActions(data, 'delete');
      }
    });
  }

  isEmpty(val: any) {
    if (val !== undefined || val !== null || val !== '') {
      val = val.trim();
    }
    return val === undefined || val === null || val === '';
  }
}
